import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Check, X, MapPin, Route, Flag, PawPrint, ArrowRight } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { useClientAuth } from "@/context/ClientAuthContext";
import { trackAbandon, trackComplete } from "@/lib/abandon";

const money = (n) => `$${Number(n).toLocaleString()}`;
const range = (r) => `${money(r.min)}–${money(r.max)}`;

function NumInput({ label, value, onChange, min = 0, max = 99, testid, hint }) {
  return (
    <div className="flex items-center justify-between rounded-xl bg-sand-100 px-5 py-4">
      <div>
        <span className="text-sm font-medium text-charcoal-700">{label}</span>
        {hint && <p className="text-xs text-charcoal-500">{hint}</p>}
      </div>
      <input
        data-testid={testid}
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        value={value}
        onChange={(e) => {
          const v = e.target.value === "" ? "" : Math.max(min, Math.min(max, Number(e.target.value) || 0));
          onChange(v === "" ? 0 : v);
        }}
        className="w-24 rounded-lg border border-sand-300 bg-white px-3 py-2 text-center text-sm font-medium text-charcoal-800 focus:outline-none focus:ring-2 focus:ring-sage/50"
      />
    </div>
  );
}

function PackageCard({ id, pkg, price, selected, onSelect, onBook, testid }) {
  return (
    <div
      role="button"
      tabIndex={0}
      data-testid={testid}
      onClick={onSelect}
      onKeyDown={(e) => e.key === "Enter" && onSelect()}
      className={`text-left cursor-pointer rounded-3xl border-2 p-7 lg:p-8 transition-all ${selected ? "border-sage bg-sage/5 shadow-[0_8px_32px_rgba(63,91,76,0.12)]" : "border-sand-200 bg-white hover:border-sand-300"}`}
    >
      <div className="flex items-start justify-between gap-3">
        <h3 className="font-serif text-xl text-charcoal-900">{pkg.name}</h3>
        <span className={`shrink-0 mt-1 h-5 w-5 rounded-full border-2 flex items-center justify-center ${selected ? "border-sage bg-sage" : "border-sand-300"}`}>
          {selected && <Check size={12} className="text-white" />}
        </span>
      </div>
      <p className="mt-2 text-sm text-charcoal-600 leading-relaxed">{pkg.short_description}</p>
      <p className="mt-3 text-sm font-medium text-clay-700" data-testid={`${testid}-price`}>{price}</p>
      <div className="mt-5 space-y-1.5">
        <p className="text-xs font-medium uppercase tracking-wider text-charcoal-500">What's included</p>
        {(pkg.included || []).map((item, i) => (
          <p key={i} className="flex items-start gap-2 text-xs text-charcoal-700">
            <Check size={13} className="mt-0.5 shrink-0 text-sage" /> {item}
          </p>
        ))}
      </div>
      {(pkg.not_included || []).length > 0 && (
        <div className="mt-4 space-y-1.5">
          <p className="text-xs font-medium uppercase tracking-wider text-charcoal-500">Not included</p>
          {pkg.not_included.map((item, i) => (
            <p key={i} className="flex items-start gap-2 text-xs text-charcoal-500">
              <X size={13} className="mt-0.5 shrink-0 text-charcoal-400" /> {item}
            </p>
          ))}
        </div>
      )}
      <button
        type="button"
        data-testid={`${testid}-book`}
        onClick={(e) => { e.stopPropagation(); onBook(); }}
        className={`mt-6 w-full rounded-full px-6 py-3 text-sm font-medium transition-colors ${selected ? "bg-terracotta text-white hover:bg-terracotta-hover" : "bg-charcoal-800 text-white hover:bg-charcoal-900"}`}
      >
        Book this package
      </button>
    </div>
  );
}

const getCalcSession = () => {
  let s = sessionStorage.getItem("hola_calc_session");
  if (!s) {
    s = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    sessionStorage.setItem("hola_calc_session", s);
  }
  return s;
};

export default function CostCalculator() {
  const navigate = useNavigate();
  const { client } = useClientAuth();
  const [cfg, setCfg] = useState(null);
  const [inp, setInp] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("hm_calc_inputs") || "null");
      if (saved && saved.package) return saved;
    } catch { /* ignore */ }
    return { package: "base_relocation", direction: "border_to_city", party_size: 2, pets: 0, hire_driver: false, tolls_and_tip: false, hotel_nights: 0, services: {} };
  });
  const [result, setResult] = useState(null);
  const [customText, setCustomText] = useState("");
  const [customEmail, setCustomEmail] = useState("");
  const [customSent, setCustomSent] = useState(false);
  const [routeCity] = useState(() => new URLSearchParams(window.location.search).get("route") || "");

  useEffect(() => {
    if (routeCity && cfg) {
      const t = setTimeout(() => document.getElementById("route-banner")?.scrollIntoView({ behavior: "smooth", block: "center" }), 500);
      return () => clearTimeout(t);
    }
  }, [routeCity, cfg]);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/calculator/config").then((r) => setCfg(r.data)).catch(() => setCfg({ addon_services: [], packages: {} }));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      api.post("/calculator/estimate", { ...inp, session_id: getCalcSession() }).then((res) => setResult(res.data)).catch(() => {});
      localStorage.setItem("hm_calc_inputs", JSON.stringify(inp));
      trackAbandon("pricing_calculator", client?.email ? { email: client.email, name: client.name } : {});
    }, 200);
    return () => clearTimeout(t);
  }, [inp]); // eslint-disable-line react-hooks/exhaustive-deps

  // Logged-in clients with pets on file: the pet count follows their profile.
  useEffect(() => {
    const n = client?.pets?.length || 0;
    if (n > 0) setInp((p) => (p.pets === n ? p : { ...p, pets: n }));
  }, [client]);

  const set = (key, val) => setInp((p) => ({ ...p, [key]: val }));
  const setService = (id, val) => setInp((p) => ({ ...p, services: { ...p.services, [id]: val } }));
  const selectPackage = (id) => setInp((p) => ({ ...p, package: id }));
  const applyBundle = (b) => {
    setInp((p) => ({
      ...p,
      package: "base_relocation",
      services: Object.fromEntries((b.addon_ids || []).map((id) => [id, 1])),
    }));
    toast.success(`${b.name} bundle applied — adjust anything below.`);
  };
  const addons = (result?.breakdown || []).filter((b) => b.addon);
  const isAdvice = inp.package === "advice_only";

  const pkgs = cfg?.packages || {};
  const basePkg = pkgs.base_relocation;
  const advicePkg = pkgs.advice_only;
  const baseRange = cfg?.base_relocation ? `Starting from ${range(cfg.base_relocation)}` : "";
  const advicePrice = advicePkg
    ? advicePkg.pricing_mode === "range"
      ? `Estimated ${money(advicePkg.min)}–${money(advicePkg.max)}`
      : `Flat fee ${money(advicePkg.flat_price || 0)}`
    : "";
  const selectedPkgName = isAdvice ? advicePkg?.name : basePkg?.name;

  const sendCustom = async () => {
    try {
      const { data } = await api.post("/calculator/custom-request", { text: customText, email: customEmail });
      toast.success(data.message);
      setCustomSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Please describe what you need first.");
    }
  };

  const bookPackage = (pkgId) => {
    const dirLabel = inp.direction === "city_to_border" ? "Your City → Border" : "Border → Your City";
    const pkgName = pkgId === "advice_only" ? advicePkg?.name : basePkg?.name;
    const useResult = result && result.package === pkgId;
    const addonLabels = useResult ? (result.breakdown || []).filter((b) => b.addon).map((b) => b.label) : [];
    const estimate = useResult
      ? result.total.min === result.total.max ? money(result.total.min) : `${money(result.total.min)} – ${money(result.total.max)}`
      : "";
    sessionStorage.setItem("hola_pricing_selection", JSON.stringify({
      package: pkgId, package_name: pkgName,
      direction: pkgId === "advice_only" ? "" : dirLabel,
      addons: addonLabels, estimate,
    }));
    trackComplete("pricing_calculator");
    navigate("/book");
  };

  return (
    <div data-testid="page-cost-calculator">
      <Seo
        title="Pricing & Services — Estimate Your Move to Mexico"
        description="Estimate your move to Mexico with Hola Movers' interactive cost calculator. Driving, flying, and bundle packages with transparent pricing."
        path="/cost-calculator"
        jsonLd={cfg ? {
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Hola Movers relocation packages",
          serviceType: "Relocation service",
          description: "Relocation planning, coordination, driving and pet transport from the US to Mexico with transparent package pricing.",
          areaServed: ["United States", "Mexico"],
          provider: { "@type": "Organization", name: "Hola Movers" },
          offers: Object.values(cfg.packages || {}).filter((pk) => pk.visible !== false).map((pk) => ({
            "@type": "Offer",
            name: pk.name,
            description: pk.short_description || "",
            priceCurrency: "USD",
            url: "https://www.holamovers.com/cost-calculator",
          })),
        } : undefined}
      />
      <section className="mx-auto max-w-7xl px-6 lg:px-12 pt-16 lg:pt-24 pb-8">
        <p className="text-sm uppercase tracking-[0.2em] text-clay-700 font-medium mb-4">Pricing & Services</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-charcoal-900 leading-[1.05] max-w-2xl">
          Choose your package. See your estimate.
        </h1>
        <p className="mt-6 text-lg text-charcoal-600 leading-relaxed max-w-2xl">
          Pick the level of support you need, review exactly what's included, then add only the optional services that apply to you.
        </p>
      </section>

      {/* Step 1: choose package */}
      <section className="mx-auto max-w-7xl px-6 lg:px-12 pb-10">
        {routeCity && (
          <div id="route-banner" data-testid="calc-route-banner"
            className="mb-6 flex flex-wrap items-center gap-3 rounded-2xl bg-terracotta-light border border-terracotta/30 px-5 py-4">
            <Route size={18} className="text-terracotta-700 shrink-0" />
            <p className="text-sm text-charcoal-800">
              <span className="font-medium">{routeCity} ⇄ Mérida</span> — your estimate below covers the full
              door-to-door drive on this route. Adjust travelers, pets and add-ons to see your live price.{" "}
              <Link to={`/routes/${routeCity.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, "-")}`}
                data-testid="calc-route-guide-link" className="font-medium text-terracotta underline underline-offset-2">
                Read the route guide →
              </Link>
            </p>
          </div>
        )}
        <h2 className="font-serif text-lg text-charcoal-800 mb-4">1 · Choose your package</h2>
        <div className="grid gap-6 md:grid-cols-2">
          {basePkg?.visible !== false && basePkg && (
            <PackageCard id="base_relocation" pkg={basePkg} price={baseRange}
              selected={!isAdvice} onSelect={() => selectPackage("base_relocation")}
              onBook={() => bookPackage("base_relocation")} testid="calc-pkg-base" />
          )}
          {advicePkg?.visible !== false && advicePkg && (
            <PackageCard id="advice_only" pkg={advicePkg} price={advicePrice}
              selected={isAdvice} onSelect={() => selectPackage("advice_only")}
              onBook={() => bookPackage("advice_only")} testid="calc-pkg-advice" />
          )}
        </div>
      </section>

      {/* How the drive works */}
      {!isAdvice && (
        <section className="mx-auto max-w-7xl px-6 lg:px-12 pb-10" data-testid="calc-how-it-works">
          <div className="rounded-3xl bg-sage-light p-8 lg:p-10">
            <h2 className="font-serif text-lg text-charcoal-800 mb-6">How the drive works — either direction</h2>
            <div className="grid gap-6 md:grid-cols-3">
              {[
                [MapPin, "1 · Meet & handoff", "We meet you at the US border or at your door in Mexico — whichever direction you're going."],
                [Route, "2 · We drive the route", "A planned, safe route with border-crossing guidance, tolls handled, and WhatsApp updates the whole way."],
                [Flag, "3 · Arrival handoff", "Your vehicle arrives safely at the destination — with arrival coordination and optional add-on support."],
              ].map(([Icon, title, body]) => (
                <div key={title} className="flex gap-3">
                  <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-sage-700"><Icon size={17} strokeWidth={1.8} /></span>
                  <div>
                    <p className="text-sm font-medium text-charcoal-800">{title}</p>
                    <p className="mt-1 text-xs text-charcoal-600 leading-relaxed">{body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Suggested bundles — prices computed live from the same admin rates */}
      {!isAdvice && (cfg?.bundles || []).length > 0 && (
        <section className="mx-auto max-w-7xl px-6 lg:px-12 pb-10">
          <h2 className="font-serif text-lg text-charcoal-800 mb-1">Suggested packages</h2>
          <p className="text-sm text-charcoal-500 mb-4">One-click starting points — every price comes straight from our live rates. You can adjust anything after.</p>
          <div className="grid gap-6 md:grid-cols-3">
            {cfg.bundles.map((b) => {
              const svcMap = Object.fromEntries((cfg.addon_services || []).map((s) => [s.id, s.label]));
              return (
                <div key={b.id} data-testid={`calc-bundle-${b.id}`} className={`relative rounded-3xl border bg-white p-6 flex flex-col ${b.featured ? "border-2 border-terracotta shadow-[0_8px_32px_rgba(190,91,61,0.12)]" : "border-sand-200"}`}>
                  {b.featured && (
                    <span data-testid={`calc-bundle-ribbon-${b.id}`} className="absolute -top-3 left-6 rounded-full bg-terracotta px-3 py-1 text-[11px] font-medium uppercase tracking-wider text-white">Most popular</span>
                  )}
                  <h3 className="font-serif text-lg text-charcoal-900">{b.name}</h3>
                  <p className="mt-1 text-xs text-charcoal-500 leading-relaxed">{b.description}</p>
                  {b.price && (
                    <p className="mt-3 text-sm font-medium text-clay-700" data-testid={`calc-bundle-price-${b.id}`}>
                      {b.price.min === b.price.max ? money(b.price.min) : `${money(b.price.min)}–${money(b.price.max)}`}
                    </p>
                  )}
                  <ul className="mt-3 space-y-1 flex-1">
                    <li className="flex items-start gap-2 text-xs text-charcoal-700">
                      <Check size={12} className="mt-0.5 shrink-0 text-sage" /> Driving service (either direction)
                    </li>
                    {(b.addon_ids || []).map((id) => svcMap[id] && (
                      <li key={id} className="flex items-start gap-2 text-xs text-charcoal-700">
                        <Check size={12} className="mt-0.5 shrink-0 text-sage" /> {svcMap[id]}
                      </li>
                    ))}
                  </ul>
                  <button type="button" data-testid={`calc-bundle-use-${b.id}`} onClick={() => applyBundle(b)}
                    className="mt-4 rounded-full border border-charcoal-800 px-5 py-2 text-xs font-medium text-charcoal-800 hover:bg-charcoal-800 hover:text-white transition-colors">
                    Use this bundle
                  </button>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <section className="mx-auto max-w-7xl px-6 lg:px-12 pb-24 grid gap-10 lg:grid-cols-5">
        {/* Inputs */}
        <div className="lg:col-span-3 space-y-8">
          {!isAdvice && (
            <div className="rounded-3xl bg-white p-8 lg:p-10 border border-sand-200 shadow-[0_8px_32px_rgba(44,42,41,0.06)] space-y-4">
              <h2 className="font-serif text-lg text-charcoal-800">2 · Your move</h2>
              <div className="rounded-2xl border border-sand-200 bg-sand-100 px-5 py-5">
                <span className="text-sm font-medium text-charcoal-700">Which direction are you going?</span>
                <div className="mt-3 grid grid-cols-2 gap-2" data-testid="calc-direction">
                  <button type="button" data-testid="calc-dir-border-to-city" onClick={() => set("direction", "border_to_city")}
                    className={`rounded-xl border px-4 py-3 text-sm font-medium transition-colors ${inp.direction === "border_to_city" ? "border-sage bg-sage/10 text-charcoal-800" : "border-sand-200 bg-white text-charcoal-600 hover:border-sand-300"}`}>
                    Border → Your City
                    <span className="block text-xs font-normal text-charcoal-500 mt-0.5">Moving into Mexico</span>
                  </button>
                  <button type="button" data-testid="calc-dir-city-to-border" onClick={() => set("direction", "city_to_border")}
                    className={`rounded-xl border px-4 py-3 text-sm font-medium transition-colors ${inp.direction === "city_to_border" ? "border-sage bg-sage/10 text-charcoal-800" : "border-sand-200 bg-white text-charcoal-600 hover:border-sand-300"}`}>
                    Your City → Border
                    <span className="block text-xs font-normal text-charcoal-500 mt-0.5">Heading back to the US</span>
                  </button>
                </div>
              </div>
              <NumInput label="People moving" value={inp.party_size} onChange={(v) => set("party_size", Math.max(1, v))} min={1} max={12} testid="calc-party" />
              <NumInput label="Number of pets" value={inp.pets} onChange={(v) => set("pets", v)} min={0} max={10} testid="calc-pets" />
              <NumInput label="Number of nights expected" value={inp.hotel_nights} onChange={(v) => set("hotel_nights", v)} min={0} max={30} testid="calc-nights" hint="Hotel nights you expect en route." />
            </div>
          )}

          {isAdvice && (
            <div className="rounded-3xl bg-white p-8 lg:p-10 border border-sand-200 shadow-[0_8px_32px_rgba(44,42,41,0.06)]" data-testid="calc-advice-note">
              <h2 className="font-serif text-lg text-charcoal-800">That's it — one flat fee.</h2>
              <p className="mt-2 text-sm text-charcoal-600 leading-relaxed">
                Flying is a simple flat-fee package: relocation advice and help, nothing else to configure.
                No add-ons, no extras — book a call and we'll get started.
              </p>
            </div>
          )}

          {!isAdvice && (
          <div className="rounded-3xl bg-white p-8 lg:p-10 border border-sand-200 shadow-[0_8px_32px_rgba(44,42,41,0.06)] space-y-5">
            <div>
              <h2 className="font-serif text-lg text-charcoal-800">3 · Optional add-on services</h2>
              <p className="text-sm text-charcoal-500 mt-1">All off by default — only selected options are added to your estimate.</p>
            </div>

            <div className={`flex items-center justify-between rounded-xl border px-5 py-4 transition-colors ${inp.tolls_and_tip ? "border-sage bg-sage/5" : "border-sand-200 bg-sand-100"}`}>
              <div>
                <span className="text-sm font-medium text-charcoal-700">Tolls &amp; tip</span>
                <p className="text-xs text-charcoal-500">Toll-road costs and driver tip for the trip. Want cashless tolling? Add the toll sticker below.</p>
              </div>
              <Switch data-testid="calc-tolls" checked={inp.tolls_and_tip} onCheckedChange={(v) => set("tolls_and_tip", v)} />
            </div>
            <div className={`flex items-center justify-between rounded-xl border px-5 py-4 transition-colors ${inp.hire_driver ? "border-sage bg-sage/5" : "border-sand-200 bg-sand-100"}`}>
              <div>
                <span className="text-sm font-medium text-charcoal-700">Hiring a driver</span>
                <p className="text-xs text-charcoal-500">A professional driver takes your vehicle so you don't have to.</p>
              </div>
              <Switch data-testid="calc-driver" checked={inp.hire_driver} onCheckedChange={(v) => set("hire_driver", v)} />
            </div>

            {(cfg?.addon_services || []).map((s) => {
              const qty = inp.services[s.id] || 0;
              return (
                <div key={s.id} data-testid={`calc-service-${s.id}`}
                  className={`flex items-center justify-between gap-3 rounded-xl border px-5 py-4 transition-colors ${qty > 0 ? "border-sage bg-sage/5" : "border-sand-200 bg-sand-100"}`}>
                  <label className="flex items-start gap-3 cursor-pointer flex-1">
                    <input type="checkbox" data-testid={`calc-service-check-${s.id}`} checked={qty > 0}
                      onChange={(e) => setService(s.id, e.target.checked ? 1 : 0)}
                      className="mt-1 h-4 w-4 accent-[#6F8F72]" />
                    <span>
                      <span className="text-sm font-medium text-charcoal-700">{s.label}</span>
                      {s.description && <p className="text-xs text-charcoal-500 mt-0.5">{s.description}</p>}
                      <p className="text-xs text-sage-700 mt-0.5">{money(s.min)}–{money(s.max)}{s.per_unit ? ` per ${s.unit_label || "unit"}` : ""}</p>
                    </span>
                  </label>
                  {s.per_unit && qty > 0 && (
                    <input type="number" min={1} max={20} value={qty} data-testid={`calc-service-qty-${s.id}`}
                      onChange={(e) => setService(s.id, Math.max(1, Math.min(20, Number(e.target.value) || 1)))}
                      className="w-20 rounded-lg border border-sand-300 bg-white px-2 py-2 text-center text-sm" />
                  )}
                </div>
              );
            })}
          </div>
          )}

          {!isAdvice && (
          <div className="rounded-3xl bg-white p-8 lg:p-10 border border-sand-200 shadow-[0_8px_32px_rgba(44,42,41,0.06)] space-y-4">
            <div>
              <h2 className="font-serif text-lg text-charcoal-800">Need something specific?</h2>
              <p className="text-sm text-charcoal-500 mt-1">Tell us about services unique to your situation — we'll price it personally and include it in your consultation quote.</p>
            </div>
            {customSent ? (
              <p className="rounded-xl bg-sage/10 px-5 py-4 text-sm text-charcoal-700" data-testid="custom-request-sent">
                Got it — we'll review your request and include exact pricing in your consultation quote.
              </p>
            ) : (
              <>
                <textarea data-testid="custom-request-text" rows={3} value={customText} onChange={(e) => setCustomText(e.target.value)}
                  placeholder="e.g. I need help moving a piano, or setting up school enrollment for my kids…"
                  className="w-full rounded-xl border border-sand-300 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-sage/50" />
                <div className="flex flex-wrap gap-3">
                  <input data-testid="custom-request-email" type="email" value={customEmail} onChange={(e) => setCustomEmail(e.target.value)}
                    placeholder="Your email (optional)"
                    className="flex-1 min-w-[200px] rounded-xl border border-sand-300 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-sage/50" />
                  <button data-testid="custom-request-send" onClick={sendCustom}
                    className="rounded-full bg-charcoal-800 px-6 py-3 text-sm font-medium text-white hover:bg-charcoal-900 transition-colors">
                    Request custom pricing
                  </button>
                </div>
              </>
            )}
          </div>
          )}
        </div>

        {/* Estimate summary */}
        <div className="lg:col-span-2">
          <div className="sticky top-28 rounded-3xl bg-night p-8 lg:p-10 text-sand-100">
            <p className="text-sm uppercase tracking-[0.2em] text-sage mb-1">Estimate summary</p>
            {selectedPkgName && <p className="text-sm text-sand-100/70 mb-3" data-testid="calc-selected-pkg">{selectedPkgName}</p>}
            {result ? (
              <>
                <p data-testid="calc-total" className="font-serif text-4xl lg:text-5xl font-light leading-tight">
                  {money(result.total.min)}
                  {result.total.min !== result.total.max && (
                    <>
                      <span className="text-sand-100/50"> – </span>
                      {money(result.total.max)}
                    </>
                  )}
                </p>
                <p className="mt-2 text-sm text-sand-100/60">{result.buffer_pct > 0 ? `Includes a ${result.buffer_pct}% planning buffer.` : "Flat fee — advice and help only."}</p>
                <p className="mt-2 text-sm text-sand-100/70" data-testid="calc-value-anchor">
                  For context: a cross-country US move alone typically runs $4,000–$9,000 — this covers an international move with hands-on support.
                </p>
                <div className="mt-8 space-y-3 border-t border-white/10 pt-6">
                  <div className="flex justify-between text-sm" data-testid="calc-base-line">
                    <span className="text-sand-100/80">Package price</span>
                    <span className="text-sand-100">{result.base_total.min === result.base_total.max ? money(result.base_total.min) : range(result.base_total)}</span>
                  </div>
                  {addons.map((b, i) => (
                    <div key={i} className="flex justify-between text-sm" data-testid={`calc-addon-${i}`}>
                      <span className="text-sand-100/60 pl-3">+ {b.label}</span>
                      <span className="text-sand-100/80">{range(b)}</span>
                    </div>
                  ))}
                  {addons.length > 0 && (
                    <div className="flex justify-between text-sm border-t border-white/10 pt-3" data-testid="calc-addons-subtotal">
                      <span className="text-sand-100/80">Add-ons subtotal</span>
                      <span className="text-sand-100">{range(result.addons_total)}</span>
                    </div>
                  )}
                  {addons.length === 0 && (
                    <p className="text-xs text-sand-100/50">No add-ons selected — this is the package-only estimate.</p>
                  )}
                </div>
                {result.disclaimer && (
                  <div className="mt-6 rounded-xl bg-white/5 px-4 py-3" data-testid="calc-disclaimer">
                    <p className="text-xs font-medium uppercase tracking-wider text-sand-100/60 mb-1">Important pricing note</p>
                    <p className="text-xs leading-relaxed text-sand-100/70">{result.disclaimer}</p>
                  </div>
                )}
              </>
            ) : (
              <p className="text-sand-100/60">Calculating…</p>
            )}
            <button type="button" onClick={() => bookPackage(inp.package)} data-testid="calc-book-cta" className="mt-8 block w-full rounded-full bg-terracotta px-6 py-3 text-center font-medium text-white hover:bg-terracotta-hover transition-colors">
              Book this package
            </button>
            {inp.pets > 0 && (
              <div className="mt-5 rounded-2xl bg-white/5 px-5 py-4" data-testid="calc-pets-banner">
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-terracotta/20 text-terracotta">
                    <PawPrint size={16} strokeWidth={1.75} />
                  </span>
                  <div>
                    <p className="text-sm font-medium text-sand-100">Moving with {inp.pets > 1 ? "pets" : "a pet"}?</p>
                    <p className="mt-0.5 text-xs leading-relaxed text-sand-100/60">
                      Vaccines, paperwork, travel-day comfort — grab our free pet checklist.
                    </p>
                    <Link
                      to="/pets"
                      data-testid="calc-pets-banner-cta"
                      className="group mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-terracotta hover:text-sand-100 transition-colors"
                    >
                      Get the free pet checklist
                      <ArrowRight size={13} className="transition-transform group-hover:translate-x-0.5" />
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
